import React, { useState, useEffect } from 'react';
import { ImageIcon, Palette, Save, Loader2, RotateCcw, Mail } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

interface BrandingData {
  logo_url: string | null;
  logo_small_url: string | null;
  login_logo_url: string | null;
  email_logo_url: string | null;
  favicon_url: string | null;
  primary_color: string | null;
  accent_color: string | null;
  show_powered_by: boolean;
  company_name: string | null;
  support_email: string | null;
  company_address: string | null;
}

interface Props {
  agencyId: string;
  agencyName: string;
}

const AgencyBrandingTab: React.FC<Props> = ({ agencyId, agencyName }) => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState<BrandingData>({
    logo_url: null,
    logo_small_url: null,
    login_logo_url: null,
    email_logo_url: null,
    favicon_url: null,
    primary_color: null,
    accent_color: null,
    show_powered_by: true,
    company_name: null,
    support_email: null,
    company_address: null
  });
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  useEffect(() => {
    loadBranding();
  }, [agencyId]);

  const loadBranding = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/agencies/${agencyId}/branding`);
      setBranding(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load branding');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiService.put(`/agencies/${agencyId}/branding`, branding);
      showSuccess('Branding updated successfully');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all branding to defaults? This will remove all custom logos, colors, and email settings.')) {
      setBranding({
        logo_url: null,
        logo_small_url: null,
        login_logo_url: null,
    email_logo_url: null,
        favicon_url: null,
        primary_color: null,
        accent_color: null,
        show_powered_by: true,
        company_name: null,
        support_email: null,
        company_address: null
      });
    }
  };

  const inputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--v2-surface)',
    border: `1px solid ${focused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none'
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px'
  };

  const buttonStyle = (hovered: boolean, variant: 'primary' | 'secondary' = 'primary'): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: variant === 'primary'
      ? hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)'
      : hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    color: variant === 'primary' ? '#ffffff' : 'var(--v2-text)',
    border: variant === 'primary' ? 'none' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  });

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Loader2 size={24} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} color="var(--v2-primary)" />
        <p style={{ color: 'var(--v2-text-tertiary)', marginTop: '12px' }}>Loading branding...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Logos Section */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <ImageIcon size={20} strokeWidth={1.5} color="var(--v2-primary)" />
          <h3 style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>
            Logos
          </h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={labelStyle}>Main Logo URL</label>
            <input
              type="text"
              value={branding.logo_url || ''}
              onChange={(e) => setBranding({ ...branding, logo_url: e.target.value || null })}
              onFocus={() => setFocusedInput('logo_url')}
              onBlur={() => setFocusedInput(null)}
              placeholder="https://example.com/logo.png"
              style={inputStyle(focusedInput === 'logo_url')}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text)', marginTop: '4px' }}>
              Used in sidebar (expanded) and emails. Recommended: 200x50px
            </p>
            {branding.logo_url && (
              <div style={{ marginTop: '8px', padding: '12px', backgroundColor: 'var(--v2-surface-alt)', borderRadius: 'var(--radius-sm)' }}>
                <img src={branding.logo_url} alt="Logo preview" style={{ maxHeight: '40px', maxWidth: '100%' }} />
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Small Logo / Icon URL</label>
            <input
              type="text"
              value={branding.logo_small_url || ''}
              onChange={(e) => setBranding({ ...branding, logo_small_url: e.target.value || null })}
              onFocus={() => setFocusedInput('logo_small_url')}
              onBlur={() => setFocusedInput(null)}
              placeholder="https://example.com/icon.png"
              style={inputStyle(focusedInput === 'logo_small_url')}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text)', marginTop: '4px' }}>
              Used in sidebar (collapsed). Recommended: 40x40px square
            </p>
            {branding.logo_small_url && (
              <div style={{ marginTop: '8px', padding: '12px', backgroundColor: 'var(--v2-surface-alt)', borderRadius: 'var(--radius-sm)' }}>
                <img src={branding.logo_small_url} alt="Icon preview" style={{ maxHeight: '40px', maxWidth: '40px' }} />
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle}>Login Page Logo URL</label>
            <input
              type="text"
              value={branding.login_logo_url || ''}
              onChange={(e) => setBranding({ ...branding, login_logo_url: e.target.value || null })}
              onFocus={() => setFocusedInput('login_logo_url')}
              onBlur={() => setFocusedInput(null)}
              placeholder="https://example.com/login-logo.png"
              style={inputStyle(focusedInput === 'login_logo_url')}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text)', marginTop: '4px' }}>
              Displayed on login page. Falls back to main logo if not set.
            </p>
          </div>
          <div>
            <label style={labelStyle}>Email Header Logo URL</label>
            <input
              type="text"
              value={branding.email_logo_url || ''}
              onChange={(e) => setBranding({ ...branding, email_logo_url: e.target.value || null })}
              onFocus={() => setFocusedInput('email_logo_url')}
              onBlur={() => setFocusedInput(null)}
              placeholder="https://example.com/logo-white.png"
              style={inputStyle(focusedInput === 'email_logo_url')}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text-muted)', marginTop: '4px' }}>
              Shown in email headers. Use a white or light version of your logo — email headers use your brand color as background.
            </p>
          </div>

          <div>
            <label style={labelStyle}>Favicon URL</label>
            <input
              type="text"
              value={branding.favicon_url || ''}
              onChange={(e) => setBranding({ ...branding, favicon_url: e.target.value || null })}
              onFocus={() => setFocusedInput('favicon_url')}
              onBlur={() => setFocusedInput(null)}
              placeholder="https://example.com/favicon.ico"
              style={inputStyle(focusedInput === 'favicon_url')}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text)', marginTop: '4px' }}>
              Browser tab icon. Recommended: 32x32px .ico or .png
            </p>
          </div>
        </div>
      </div>

      {/* Colors Section */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Palette size={20} strokeWidth={1.5} color="var(--v2-primary)" />
          <h3 style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>
            Colors
          </h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={labelStyle}>Primary Color</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="color"
                value={branding.primary_color || '#2563eb'}
                onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                style={{
                  width: '44px',
                  height: '44px',
                  padding: '2px',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={branding.primary_color || ''}
                onChange={(e) => setBranding({ ...branding, primary_color: e.target.value || null })}
                onFocus={() => setFocusedInput('primary_color')}
                onBlur={() => setFocusedInput(null)}
                placeholder="#2563eb"
                style={{ ...inputStyle(focusedInput === 'primary_color'), flex: 1 }}
              />
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text)', marginTop: '4px' }}>
              Used for buttons, links, and accents
            </p>
          </div>

          <div>
            <label style={labelStyle}>Accent Color</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="color"
                value={branding.accent_color || '#818cf8'}
                onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                style={{
                  width: '44px',
                  height: '44px',
                  padding: '2px',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={branding.accent_color || ''}
                onChange={(e) => setBranding({ ...branding, accent_color: e.target.value || null })}
                onFocus={() => setFocusedInput('accent_color')}
                onBlur={() => setFocusedInput(null)}
                placeholder="#818cf8"
                style={{ ...inputStyle(focusedInput === 'accent_color'), flex: 1 }}
              />
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text)', marginTop: '4px' }}>
              Secondary color for highlights
            </p>
          </div>
        </div>
      </div>

      {/* Email Identity Section */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <Mail size={20} strokeWidth={1.5} color="var(--v2-primary)" />
          <h3 style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>
            Email Identity
          </h3>
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-muted)', margin: '0 0 20px 0', lineHeight: 1.5 }}>
          Required for CAN-SPAM and GDPR compliance. A valid reply-to address and physical mailing address
          must appear in all commercial emails sent under your brand.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={labelStyle}>Support Email Address</label>
            <input
              type="email"
              value={branding.support_email || ''}
              onChange={(e) => setBranding({ ...branding, support_email: e.target.value || null })}
              onFocus={() => setFocusedInput('support_email')}
              onBlur={() => setFocusedInput(null)}
              placeholder="support@yourdomain.com"
              style={inputStyle(focusedInput === 'support_email')}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text-muted)', marginTop: '4px' }}>
              Shown in email footers and used as the reply-to address for your white-label emails.
            </p>
          </div>

          <div>
            <label style={labelStyle}>Company Mailing Address</label>
            <textarea
              value={branding.company_address || ''}
              onChange={(e) => setBranding({ ...branding, company_address: e.target.value || null })}
              onFocus={() => setFocusedInput('company_address')}
              onBlur={() => setFocusedInput(null)}
              placeholder={'123 Main St\nSuite 100\nCity, State 00000'}
              rows={3}
              style={{
                ...inputStyle(focusedInput === 'company_address'),
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: 1.5
              }}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text-muted)', marginTop: '4px' }}>
              Physical address displayed in email footers. Required by CAN-SPAM. Max 500 characters.
            </p>
          </div>
        </div>
      </div>

      {/* Email Footer Section */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--v2-text)', margin: '0 0 16px 0' }}>
          Email Footer
        </h3>

        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={branding.show_powered_by}
            onChange={(e) => setBranding({ ...branding, show_powered_by: e.target.checked })}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <div>
            <p style={{ fontSize: '0.875rem', color: 'var(--v2-text)', margin: 0 }}>
              Show "Powered by DashboardFox, a 5000fish, Inc. product"
            </p>
            <p style={{ fontSize: '0.75rem', color: 'var(--v2-text)', margin: '4px 0 0 0' }}>
              Displayed in email footers when using white-label branding
            </p>
          </div>
        </label>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button
          onClick={handleReset}
          onMouseEnter={() => setHoveredButton('reset')}
          onMouseLeave={() => setHoveredButton(null)}
          style={buttonStyle(hoveredButton === 'reset', 'secondary')}
        >
          <RotateCcw size={16} strokeWidth={1.5} />
          Reset to Defaults
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          onMouseEnter={() => setHoveredButton('save')}
          onMouseLeave={() => setHoveredButton(null)}
          style={{
            ...buttonStyle(hoveredButton === 'save', 'primary'),
            opacity: saving ? 0.6 : 1,
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? (
            <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Save size={16} strokeWidth={1.5} />
          )}
          {saving ? 'Saving...' : 'Save Branding'}
        </button>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AgencyBrandingTab;
